import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { Block } from '../classes/Block';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import MergeIcon from '@mui/icons-material/Merge';
import BlockCreationPopup from './BlockCreationPopup';
import './BlockTabs.css';
import { t } from '@lingui/core/macro';
import { CardGroup, CardId, buildBlockCardMap } from '../utils/cardGroups';

interface BlockTabsProps {
  blocks: Block[];
  selectedBlockIndex: number | null;
  onBlockSelect: (index: number | null) => void;
  onBlocksChange: (newBlocks: Block[]) => void | Promise<void>;
  maxTabsHeight?: number;
  tabsTopOffset?: number;
  isCardsMode?: boolean;
  cardGroups?: CardGroup[];
  onSplitCardAt?: (blockId: string) => void;
  onMergeCardWithPrevious?: (cardId: CardId) => void;
}

const BlockTabs: React.FC<BlockTabsProps> = ({
  blocks,
  selectedBlockIndex,
  onBlockSelect,
  onBlocksChange,
  maxTabsHeight,
  tabsTopOffset,
  isCardsMode = false,
  cardGroups = [],
  onSplitCardAt,
  onMergeCardWithPrevious,
}) => {
  const [editingBlockIndex, setEditingBlockIndex] = useState<number | null>(null);
  const [editingName, setEditingName] = useState<string>('');
  const [hoveredBlockIndex, setHoveredBlockIndex] = useState<number | null>(null);
  const [showCreationPopup, setShowCreationPopup] = useState<boolean>(false);

  const [animatingBlocks, setAnimatingBlocks] = useState<Set<number>>(new Set());
  const tabsListRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (selectedBlockIndex === null || !tabsListRef.current) return;
    const selectedTab: HTMLElement | null = tabsListRef.current.querySelector(
      `.block-tab[data-block-index="${selectedBlockIndex}"]`
    );
    selectedTab?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [selectedBlockIndex]);

  const visibleBlocks = useMemo(
    () => blocks
      .map((block, blockIndex) => ({ block, blockIndex }))
      .filter(({ block }) => block?.name?.trim() && block.name !== "undefined"),
    [blocks]
  );

  const handleBlockClick = useCallback((blockIndex: number | null) => {
    onBlockSelect(blockIndex);
  }, [onBlockSelect]);

  const handleEditClick = useCallback((e: React.MouseEvent, blockIndex: number) => {
    e.stopPropagation();
    const block = blocks[blockIndex];
    if (block) {
      setEditingBlockIndex(blockIndex);
      setEditingName(block.name);
    }
  }, [blocks]);

  const handleDeleteClick = useCallback((e: React.MouseEvent, blockIndex: number) => {
    e.stopPropagation();
    const newBlocks = blocks.filter((_, index) => index !== blockIndex);
    const result = onBlocksChange(newBlocks);
    if (result instanceof Promise) {
      result.catch(error => {
        console.error('Error in onBlocksChange:', error);
      });
    }

    if (selectedBlockIndex !== null) {
      if (blockIndex === selectedBlockIndex) {
        onBlockSelect(newBlocks.length > 0 ? 0 : null);
      } else if (blockIndex < selectedBlockIndex) {
        onBlockSelect(selectedBlockIndex - 1);
      }
    }
  }, [blocks, selectedBlockIndex, onBlocksChange, onBlockSelect]);

  const handleRenameSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    if (editingBlockIndex !== null && editingName.trim()) {
      const newBlocks = [...blocks];
      newBlocks[editingBlockIndex] = Block.create({
        ...newBlocks[editingBlockIndex],
        name: editingName.trim()
      });
      const result = onBlocksChange(newBlocks);
      if (result instanceof Promise) {
        result.catch(error => {
          console.error('Error in onBlocksChange:', error);
        });
      }
      setEditingBlockIndex(null);
      setEditingName('');
    }
  }, [editingBlockIndex, editingName, blocks, onBlocksChange]);

  const handleRenameCancel = useCallback(() => {
    setEditingBlockIndex(null);
    setEditingName('');
  }, []);

  const handleAddBlock = useCallback(() => {
    setShowCreationPopup(true);
  }, []);

  const handleCreateEmptyBlock = useCallback(() => {
    const newBlock = Block.create({
      name: `Block ${blocks.length + 1}`,
      blockNameColor: '#ffffff',
      labelFontColor: '#ffffff',
      valueFontColor: '#ffffff',
      dataRows: [{ col0: '', col1: 'New', col2: 'Block' }]
    });
    const newBlocks = [...blocks, newBlock];
    const result = onBlocksChange(newBlocks);
    if (result instanceof Promise) {
      result.catch(error => {
        console.error('Error in onBlocksChange:', error);
      });
    }
    const newBlockIndex = newBlocks.length - 1;
    onBlockSelect(newBlockIndex);
    setEditingBlockIndex(newBlockIndex);
    setEditingName(newBlock.name);
  }, [blocks, onBlocksChange, onBlockSelect]);

  const handleCloneBlock = useCallback((sourceBlock: Block) => {
    const clonedBlock = Block.create({
      ...sourceBlock,
      id: undefined,
      name: `${sourceBlock.name} ` + t`(Copy)`,
      dataRows: sourceBlock.dataRows.map(row => ({ ...row }))
    });
    
    const newBlocks = [...blocks, clonedBlock];
    const result = onBlocksChange(newBlocks);
    if (result instanceof Promise) {
      result.catch(error => {
        console.error('Error in onBlocksChange:', error);
      });
    }
    const newBlockIndex = newBlocks.length - 1;
    onBlockSelect(newBlockIndex);
    setEditingBlockIndex(newBlockIndex);
    setEditingName(clonedBlock.name);
  }, [blocks, onBlocksChange, onBlockSelect]);

  const handleMoveBlock = useCallback((e: React.MouseEvent, blockIndex: number, direction: 'up' | 'down') => {
    e.stopPropagation();
    const newIndex = direction === 'up' ? blockIndex - 1 : blockIndex + 1;

    if (newIndex < 0 || newIndex >= blocks.length) return;

    setAnimatingBlocks(new Set([blockIndex, newIndex]));

    setTimeout(() => {
      const newBlocks = [...blocks];
      [newBlocks[blockIndex], newBlocks[newIndex]] = [newBlocks[newIndex], newBlocks[blockIndex]];
      const result = onBlocksChange(newBlocks);
      if (result instanceof Promise) result.catch(err => console.error('Error in onBlocksChange:', err));

      if (selectedBlockIndex !== null) {
        if (selectedBlockIndex === blockIndex) onBlockSelect(newIndex);
        else if (selectedBlockIndex === newIndex) onBlockSelect(blockIndex);
      }

      setTimeout(() => setAnimatingBlocks(new Set()), 420);
    }, 20);
  }, [blocks, selectedBlockIndex, onBlocksChange, onBlockSelect]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      handleRenameCancel();
    }
  }, [handleRenameCancel]);

  const tabsStyle: React.CSSProperties = {
    ...(maxTabsHeight ? { maxHeight: `${maxTabsHeight}px` } : {}),
    ...(tabsTopOffset ? { marginTop: `${tabsTopOffset}px` } : {}),
  };

  const blockCardMap = useMemo(
    () => (isCardsMode ? buildBlockCardMap(cardGroups) : new Map()),
    [isCardsMode, cardGroups]
  );

  return (
    <div className="block-tabs-container">
      <div className="block-tabs" style={tabsStyle} ref={tabsListRef}>
        {visibleBlocks.map(({ block, blockIndex }) => {
          const isSelected: boolean = selectedBlockIndex === blockIndex;
          const isEditing: boolean = editingBlockIndex === blockIndex;
          const isHovered: boolean = hoveredBlockIndex === blockIndex;

          let animationClass: string = '';
          if (animatingBlocks.has(blockIndex)) {
            const arr: number[] = Array.from(animatingBlocks);
            if (arr.length === 2) {
              const other: number | undefined = arr.find(i => i !== blockIndex);
              if (other !== undefined) animationClass = blockIndex < other ? 'moving-down' : 'moving-up';
            }
          }

          const cardGroup: CardGroup | undefined = isCardsMode ? blockCardMap.get(block.id) : undefined;
          const cardColor: string = cardGroup?.color ?? 'transparent';
          const isFirstInCard: boolean = isCardsMode && cardGroup !== undefined && cardGroup.blockIds[0] === block.id;
          const isFirstCard: boolean = isFirstInCard && cardGroups[0]?.id === cardGroup?.id;
          const canSplit: boolean = isCardsMode && cardGroup !== undefined && !isFirstInCard;
          const canMerge: boolean = isCardsMode && isFirstInCard && !isFirstCard;

          return (
            <div
              key={block.id}
              data-block-index={blockIndex}
              className={`block-tab ${isSelected ? 'active' : ''} ${animationClass}${isCardsMode ? ' cards-mode' : ''}`}
              style={isCardsMode && cardGroup ? { '--card-color': cardColor } as React.CSSProperties : undefined}
              onClick={() => handleBlockClick(blockIndex)}
              onMouseEnter={() => setHoveredBlockIndex(blockIndex)}
              onMouseLeave={() => setHoveredBlockIndex(null)}
            >
              {isCardsMode && cardGroup && (
                <span className="card-accent-strip" style={{ background: cardColor }} />
              )}
              {isEditing ? (
                <form onSubmit={handleRenameSubmit} onClick={(e) => e.stopPropagation()}>
                  <input
                    type="text"
                    value={editingName}
                    onChange={(e) => setEditingName(e.target.value)}
                    onBlur={handleRenameCancel}
                    onKeyDown={handleKeyDown}
                    autoFocus
                    className="block-rename-input"
                  />
                </form>
              ) : (
                <>
                  <div className="block-name-row">
                    {isFirstInCard && (
                      <span className="card-badge" style={{ background: cardColor }}>
                        {cardGroup!.name}
                      </span>
                    )}
                    <span className="block-name" title={block.name}>{block.name}</span>
                  </div>
                  {isHovered && (
                    <div className="block-actions">
                      <button
                        className="block-action-btn move-up-btn"
                        onClick={(e) => handleMoveBlock(e, blockIndex, 'up')}
                        title="Move block up"
                        disabled={blockIndex === 0}
                      >
                        <KeyboardArrowUpIcon fontSize="small" />
                      </button>
                      <button
                        className="block-action-btn move-down-btn"
                        onClick={(e) => handleMoveBlock(e, blockIndex, 'down')}
                        title="Move block down"
                        disabled={blockIndex === blocks.length - 1}
                      >
                        <KeyboardArrowDownIcon fontSize="small" />
                      </button>
                      <button
                        className="block-action-btn edit-btn"
                        onClick={(e) => handleEditClick(e, blockIndex)}
                        title="Rename block"
                      >
                        <EditIcon fontSize="small" />
                      </button>
                      <button
                        className="block-action-btn delete-btn"
                        onClick={(e) => handleDeleteClick(e, blockIndex)}
                        title="Delete block"
                      >
                        <DeleteIcon fontSize="small" />
                      </button>
                      {canSplit && onSplitCardAt && (
                        <button
                          className="block-action-btn split-card-btn"
                          onClick={(e) => { e.stopPropagation(); onSplitCardAt(block.id); }}
                          title="Start new card here"
                        >
                          <CallSplitIcon fontSize="small" />
                        </button>
                      )}
                      {canMerge && onMergeCardWithPrevious && cardGroup && (
                        <button
                          className="block-action-btn merge-card-btn"
                          onClick={(e) => { e.stopPropagation(); onMergeCardWithPrevious(cardGroup.id); }}
                          title="Merge with previous card"
                        >
                          <MergeIcon fontSize="small" />
                        </button>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>
      <button
        className="add-block-btn"
        onClick={handleAddBlock}
        title="Add new block"
      >
        <AddIcon fontSize="small" />
      </button>

      <BlockCreationPopup
        isOpen={showCreationPopup}
        onClose={() => setShowCreationPopup(false)}
        onCreateEmpty={handleCreateEmptyBlock}
        onCloneBlock={handleCloneBlock}
        existingBlocks={visibleBlocks.map(({ block }) => block)}
      />
    </div>
  );
};

export default BlockTabs; 